import { useAtomValue } from 'jotai';
import { teal } from '@carbon/colors';
import { Layer, Source } from 'react-map-gl';
import type { CirclePaint, LinePaint } from 'mapbox-gl';
import { circlePaint } from 'components/map/utils';
import { EVAL3_SCOPED_LAYER_IDS } from 'components/map';
import {
  scopedMapLayersAtom,
  typeCurveOverridesAtom,
  inventoryPackageOverridesAtom,
  typeCurveScenarioIdAtom,
} from './atoms';
import { useLtdSlotHighlightFilter } from './hooks';
import { packageMapSourceUrl } from './utils';

/**
 * LTD place paint — a DASHED `teal[60]` line, deliberately distinct from the SOLID `blue[60]` of
 * the drilled wells (the Slot layer) so a planned location reads as "not drilled yet" at a
 * glance. Thin so it doesn't dominate the real geometry. Module consts (like the sibling layers'
 * paints) so their identity is stable and react-map-gl doesn't re-apply paint and jank the zoom.
 * Exported because the Manage Type Curves modal map draws the same sticks — one definition, so the
 * two maps cannot render an LTD place differently.
 */
const LTD_COLOR = teal[60];
export const ltdPaint: LinePaint = {
  'line-color': LTD_COLOR,
  'line-width': 1,
  'line-dasharray': [2, 2],
};
export const ltdPointPaint: CirclePaint = circlePaint(LTD_COLOR);

interface LtdSlotLayerProps {
  /** The package whose scoped LTD tiles are rendered (from `PackageMap`). */
  packageId: number;
  /** The eval-calc id (calc mode) or null — threaded into the tile's filterSet resolution. */
  calculationId: number | null;
}

/**
 * Package-scoped Type Curve LTD layer (INS-10669) — the `insights_ltd_slots` MVT tile, one
 * LineString per planned location. These are the first well-less slots with geometry of their
 * own; before this the map could only fall back to the unit polygon, which drew every place in a
 * unit on the same footprint.
 *
 * The tile emits two MVT layers and the zoom decides which is populated (see
 * `Package::Maps::Tiles::InsightsLTDSlots`): `ltd-stick` at detail zoom, `ltd-point` (the
 * surface end) below it, so the zoomed-out "fit all items" view stays light. Both are mounted
 * unconditionally — an empty source-layer simply draws nothing — which keeps the source stable
 * across zoom.
 *
 * Like the other scoped layers this is a superset narrowed server-side by the filterSet token
 * baked into the TileJSON, so it cannot show a place the table would not list. Visibility rides
 * the `ltd` key of `scopedMapLayersAtom` via `layout.visibility`, so toggling never refetches.
 * When a row is pinned (or a filter / spatial scope narrows the page) the layer is additionally
 * filtered to that set's `slot_id`s — a place has no UWI, so `slotIds` is the only identifier
 * that can select one; nothing narrowing → `['all']` → every stick draws.
 *
 * @param {LtdSlotLayerProps} props - The package id + eval-calc id the tiles are scoped to.
 * @return {JSX.Element} The LTD tile source with its line + point layers.
 */
export function LtdSlotLayer({ packageId, calculationId }: LtdSlotLayerProps) {
  const { ltd } = useAtomValue(scopedMapLayersAtom);
  const typeCurveScenarioId = useAtomValue(typeCurveScenarioIdAtom);
  // The filterSet token the sources endpoint mints is hashed over these, so the URL must
  // carry them or the layer draws the default curves' places (INS-10669).
  const typeCurveOverrides = useAtomValue(typeCurveOverridesAtom);
  const inventoryPackageOverrides = useAtomValue(inventoryPackageOverridesAtom);
  const layout = { visibility: ltd ? 'visible' : 'none' } as const;
  // Memoized in the hook — that stability is load-bearing for smooth zoom (see its comment).
  // Always a valid array, never undefined, or mapbox's addLayer validation rejects the layer.
  const filter = useLtdSlotHighlightFilter(packageId, calculationId);

  return (
    <Source
      id="eval3-ltd-slots"
      type="vector"
      // The sources endpoint, like every other scoped layer — it resolves the package's
      // baseline filterSet and bakes the token into the returned TileJSON. The scenario rides
      // along because it decides which places are members of that set.
      url={packageMapSourceUrl(
        packageId,
        'insights_ltd_slots',
        calculationId,
        typeCurveScenarioId,
        typeCurveOverrides,
        inventoryPackageOverrides
      )}
    >
      <Layer
        id={EVAL3_SCOPED_LAYER_IDS.ltd.stick}
        type="line"
        source-layer="ltd-stick"
        layout={layout}
        filter={filter}
        paint={ltdPaint}
      />
      <Layer
        id={EVAL3_SCOPED_LAYER_IDS.ltd.point}
        type="circle"
        source-layer="ltd-point"
        layout={layout}
        filter={filter}
        paint={ltdPointPaint}
      />
    </Source>
  );
}
